/**
 * Firestore shape for the rsvp subcollection under a section:
 * `sections/{sectionId}/rsvp/{userId}`.
 *
 * Each user in a section gets one doc; the doc's boolean-valued keys are
 * stringified course-week indices indicating whether the user RSVPed "yes"
 * (`true`) or "no" (`false`) for that week. A missing key means "has not
 * RSVPed yet."
 *
 * `isSectionLeader` is preserved for back-compat with rsvp logs and
 * tooling that filtered on it, but is **no longer load-bearing**: the
 * `cacheSectionLeaderRSVP` mirror trigger now gates on
 * `section.sectionLeader === userId` instead, so the flag's staleness
 * after SL swaps can no longer suppress mirroring. New code should treat
 * the section pointer as the source of truth for "is this user the SL of
 * this section."
 */
import {z} from 'zod';

import type {CourseWeekId, SectionId} from '../../../section/sectionApi';
import type {UserId} from '../../../types/branded';
import {FirestoreTimestampSchema, type FirebaseTimestamp} from '../../types';

/**
 * Firestore shape for `sections/{sectionId}/rsvp/{userId}`.
 */
export interface SectionRsvpDocument {
  /**
   * Every field keyed by a course-week index is a boolean RSVP for that
   * week. `true` means the user RSVPed yes, `false` means the user RSVPed
   * no, a missing entry means the user has not RSVPed. Keyed by
   * {@link CourseWeekId} at the TypeScript level; stored as stringified
   * integers on disk.
   *
   * **Keys are 1-based.** The first course week is `'1'`, never `'0'`.
   * `RSVP.tsx` writes `[nextSectionWeek]: newValue` where
   * `nextSectionWeek = sectionWeekIdx + 1` enforces this. Example shape
   * (real cip6 doc): `{'1': true, '2': false, isSectionLeader: true}`.
   *
   * Consumers using a 0-based week index — e.g. the reminder cron's
   * legacy `getSectionWeekNumber`, or `computeNextSectionSlot`'s
   * `sectionWeekIdx` from `@cip/core/section/slotTime` — **must add 1**
   * before keying into this doc. Indexing with `'0'` always misses the
   * first course week.
   */
  [courseWeekId: CourseWeekId]: boolean;
  /**
   * @deprecated Informational only. Stamped client-side at last-RSVP-click
   * time (see `RSVP.tsx`) and does not stay consistent under SL swaps.
   * The `cacheSectionLeaderRSVP` mirror trigger now gates on
   * `section.sectionLeader === userId` instead, which is the canonical
   * truth for "is this user the SL of this section." Treat reads of this
   * field as advisory and prefer the section pointer for any decision
   * that affects behavior.
   */
  isSectionLeader: boolean;
}

/**
 * Firestore shape for a single entry in the rsvp log subcollection at
 * `sections/{sectionId}/rsvp/{userId}/logs/{autoId}`. Written whenever a
 * user changes their RSVP for a week, for audit/debugging.
 */
export interface SectionRsvpLog {
  /** Server-timestamped write time. */
  createdAt: FirebaseTimestamp;
  /** The full rsvp doc state at the time of the change. */
  rsvp: SectionRsvpDocument;
  /** The course week (1-indexed) that changed. */
  sectionNumberChanged: number;
  /** The new RSVP value for the changed week. `null` means "no response." */
  sectionNewValue: boolean | null;
  /** The previous RSVP value for the changed week. `null` means "no
   *  response previously." */
  sectionOldValue: boolean | null;
}

// ============================================================================
// Zod schemas
// ============================================================================

/**
 * Runtime validator for {@link SectionRsvpDocument}. The catchall field
 * accepts boolean week entries; the named `isSectionLeader` field is typed
 * strictly as boolean.
 */
export const SectionRsvpDocumentSchema = z
  .object({
    isSectionLeader: z.boolean(),
  })
  .catchall(z.boolean());

/**
 * Runtime validator for {@link SectionRsvpLog}.
 */
export const SectionRsvpLogSchema = z
  .object({
    createdAt: FirestoreTimestampSchema,
    rsvp: SectionRsvpDocumentSchema,
    sectionNumberChanged: z.number(),
    sectionNewValue: z.boolean().nullable(),
    sectionOldValue: z.boolean().nullable(),
  })
  .loose();

// ============================================================================
// Paths
// ============================================================================

/** Name of the rsvp subcollection under each section document. */
export const SECTION_RSVP_SUBCOLLECTION_NAME = 'rsvp';

/** Name of the log subcollection under each rsvp document. */
export const SECTION_RSVP_LOG_SUBCOLLECTION_NAME = 'logs';

/**
 * Builds the Firestore path for the rsvp subcollection of a section.
 *
 * @param sectionId - The parent section id.
 */
export function getSectionRsvpCollectionFirebasePath(
  sectionId: SectionId | string,
): string {
  return `sections/${sectionId}/${SECTION_RSVP_SUBCOLLECTION_NAME}`;
}

/**
 * Builds the Firestore path for a single rsvp document.
 *
 * @param sectionId - The parent section id.
 * @param userId - The id of the user whose RSVP doc to address.
 */
export function getSectionRsvpDocumentFirebasePath(
  sectionId: SectionId | string,
  userId: UserId,
): string {
  return `${getSectionRsvpCollectionFirebasePath(sectionId)}/${userId}`;
}

/**
 * Builds the Firestore path for the log subcollection under a single
 * rsvp document.
 *
 * @param sectionId - The parent section id.
 * @param userId - The user id whose rsvp doc owns the logs.
 */
export function getSectionRsvpLogCollectionFirebasePath(
  sectionId: SectionId | string,
  userId: UserId,
): string {
  return `${getSectionRsvpDocumentFirebasePath(sectionId, userId)}/${SECTION_RSVP_LOG_SUBCOLLECTION_NAME}`;
}
